import type {
  BookType,
  CurrentValuationSnapshot,
  PerformanceResult,
} from "@/schemas";

export const ALL_PORTFOLIO_TABS = [
  "dashboard",
  "positions",
  "diversification",
  "transactions",
] as const;

export type PortfolioTab = (typeof ALL_PORTFOLIO_TABS)[number];

interface PortfolioTabConfigInput {
  selectedBookId: string;
  bookType?: BookType;
  isEmpty: boolean;
}

interface PortfolioHoldingsInput {
  currentPerformance: PerformanceResult | null;
  currentValuation: CurrentValuationSnapshot | null;
}

const HOLDING_VALUE_EPSILON = 0.01;
const HOLDING_QUANTITY_EPSILON = 0.0000001;

export function isPortfolioTab(value?: string): value is PortfolioTab {
  return ALL_PORTFOLIO_TABS.includes(value as PortfolioTab);
}

export function getPortfolioTabConfig({
  selectedBookId,
  bookType,
  isEmpty,
}: PortfolioTabConfigInput): {
  allowedTabs: PortfolioTab[];
  defaultTab: PortfolioTab;
  isCashAccount: boolean;
} {
  const isCashAccount = selectedBookId !== "global" && bookType === "cash";

  if (isCashAccount) {
    return {
      allowedTabs: ["dashboard", "transactions"],
      defaultTab: "dashboard",
      isCashAccount,
    };
  }

  if (selectedBookId !== "global" && isEmpty) {
    return {
      allowedTabs: ["transactions"],
      defaultTab: "transactions",
      isCashAccount,
    };
  }

  return {
    allowedTabs: [...ALL_PORTFOLIO_TABS],
    defaultTab: "dashboard",
    isCashAccount,
  };
}

export function resolvePortfolioTab(
  requestedTab: string | undefined,
  allowedTabs: PortfolioTab[],
): PortfolioTab {
  if (isPortfolioTab(requestedTab) && allowedTabs.includes(requestedTab)) {
    return requestedTab;
  }

  return allowedTabs[0] ?? "dashboard";
}

export function hasPortfolioHoldings({
  currentPerformance,
  currentValuation,
}: PortfolioHoldingsInput): boolean {
  const valuationAssets = currentValuation?.assets
    ? Object.values(currentValuation.assets)
    : [];
  if (
    valuationAssets.some(
      (asset) =>
        Math.abs(asset.quantity) > HOLDING_QUANTITY_EPSILON ||
        Math.abs(asset.value_base) > HOLDING_VALUE_EPSILON,
    )
  ) {
    return true;
  }

  const performanceAssets = currentPerformance?.data?.assets_performance
    ? Object.values(currentPerformance.data.assets_performance)
    : [];

  return performanceAssets.some(
    (asset) =>
      Math.abs(asset.current_value ?? 0) > HOLDING_VALUE_EPSILON ||
      Math.abs(asset.shares_owned ?? 0) > HOLDING_QUANTITY_EPSILON,
  );
}
